import { faAdd } from "@fortawesome/free-solid-svg-icons";
import React, { useState } from "react";
import { Button, Card, Col, Row, Stack } from "react-bootstrap";
import { postJob } from "../../../services/httpClient";
import Input from "../../ui/Input";
import JobCard from "./JobCard";
import { unsavedChanges } from "../../../App";
import IconEmbeddedButton from "../../ui/IconEmbeddedButton";

interface JobsState {
  jobs: any;
  getJobs: any;
  setOrgToSave: any;
  setErrMessage: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const Jobs = ({ jobs, getJobs, setOrgToSave, setErrMessage, setShowToast, setToastText, setToastBody }: JobsState) => {
  const [newDept, setNewDept] = useState<string>("");
  const [newJob, setNewJob] = useState<string>("");
  const [newPositionTotal, setNewPositionTotal] = useState<number>();
  const [showAddJobDept, setShowAddJobDept] = useState(false);
  const [showAddJob, setShowAddJob] = useState<string | null>(null);
  // const [disableDepartment, setDisableDepartment] = useState(false);

  const onClickAddJobDept = () => {
    setShowAddJobDept(true);
    setNewDept("");
    setNewJob("");
    setNewPositionTotal(undefined);
  };

  const onClickAddJob = (dept: string) => {
    setShowAddJob(dept);
    setNewDept(dept);
    setNewJob("");
    setNewPositionTotal(undefined);
  };

  const updateNewDept = (e: any) => {
    setNewDept(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const updateNewJob = (e: any) => {
    setNewJob(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const updateNewPositionTotal = (e: any) => {
    setNewPositionTotal(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const onClickSaveJob = (isDone: boolean = false, dept?: string) => {
    const _job = {
      name: newJob,
      department: dept ?? newDept,
      positions_total: newPositionTotal,
    };

    setErrMessage("");

    postJob(_job)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Job Created");
        setToastBody(newJob);
        setNewJob("");
        setNewPositionTotal(undefined);
        unsavedChanges.value = false;
        setOrgToSave(false);
        if (isDone) {
          setNewDept("");
          setShowAddJobDept(false);
          setShowAddJob(null);
        }
        getJobs(true);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelAddJob = () => {
    setErrMessage("");
    setShowAddJobDept(false);
    setShowAddJob(null);
    setNewDept("");
    setNewJob("");
    setNewPositionTotal(undefined);
    unsavedChanges.value = false;
    setOrgToSave(false);
  };

  return (
    <>
      {jobs.length === 0 ? (
        <p className="mt-2">No Job Records</p>
      ) : (
        <Row>
          <Col xs={12}>
            <Stack gap={3}>
              {jobs?.map((dept: any, index: number) => (
                <Card key={index} className="bg-light p-3">
                  <Row className="mb-3">
                    <Col xs={9}>
                      <div
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          width: "100%",
                        }}
                      >
                        <h6>{dept[0].department}</h6>
                      </div>
                    </Col>
                    <Col xs={3} className="d-flex justify-content-end">
                      <IconEmbeddedButton
                        onClickButton={() => onClickAddJob(dept[0].department)}
                        tooltipText="Add Job"
                        icon={faAdd}
                        disabled={showAddJobDept}
                      />
                    </Col>
                  </Row>
                  {showAddJob === dept[0].department && (
                    <Stack gap={3} className="mb-3">
                      <Row>
                        <Col xs={6}>
                          <Input
                            label="Job"
                            type="text"
                            value={newJob}
                            onUpdate={(e: any) => updateNewJob(e)}
                            hasFocus={true}
                            required={true}
                          />
                        </Col>
                        <Col xs={3}>
                          <Input
                            label="Positions #"
                            type="number"
                            value={newPositionTotal}
                            onUpdate={(e: any) => updateNewPositionTotal(e)}
                            required={true}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12} lg={8}>
                          <Stack direction="horizontal" gap={3}>
                            <Button
                              className="app-secondary-color button"
                              disabled={!newJob || !newPositionTotal}
                              onClick={() => onClickSaveJob(false, dept[0].department)}
                            >
                              Save and Add Another
                            </Button>
                            <div className="vr" />
                            <Button
                              className="app-secondary-color button"
                              disabled={!newJob || !newPositionTotal}
                              onClick={() => onClickSaveJob(true, dept[0].department)}
                            >
                              Save and Done
                            </Button>
                            <div className="vr" />
                            <Button className="app-primary-color button" onClick={() => onClickCancelAddJob()}>
                              Done
                            </Button>
                          </Stack>
                        </Col>
                      </Row>
                    </Stack>
                  )}
                  <Stack gap={2}>
                    {dept?.map((j: any, index: number) => (
                      <JobCard
                        key={`${dept[0].department}-${index}`}
                        item={j}
                        getItems={getJobs}
                        setOrgToSave={setOrgToSave}
                        setErrMessage={setErrMessage}
                        setShowToast={setShowToast}
                        setToastText={setToastText}
                        setToastBody={setToastBody}
                      />
                    ))}
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Col>
        </Row>
      )}
      {showAddJobDept !== true && (
        <Row className="mt-3">
          <Col xs={6} lg={4}>
            <Button className="app-tertiary-color button" onClick={(e) => onClickAddJobDept()} disabled={showAddJob !== null}>
              Add Department and Job
            </Button>
          </Col>
        </Row>
      )}
      {showAddJobDept === true && (
        <>
          <Row className="align-items-center my-3">
            <Col xs={3}>
              <Input label="Department" type="text" value={newDept} onUpdate={(e: any) => updateNewDept(e)} hasFocus={true} />
            </Col>
            <Col xs={6}>
              <Input label="Job" type="text" value={newJob} onUpdate={(e: any) => updateNewJob(e)} required={true} />
            </Col>
            <Col xs={3}>
              <Input
                label="Positions #"
                type="number"
                value={newPositionTotal}
                onUpdate={(e: any) => updateNewPositionTotal(e)}
                required={true}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12} lg={8}>
              <Stack direction="horizontal" gap={3}>
                <Button className="app-secondary-color button" onClick={() => onClickSaveJob(false)}>
                  Save and Add Another
                </Button>
                <div className="vr" />
                <Button className="app-secondary-color button" onClick={() => onClickSaveJob(true)}>
                  Save and Done
                </Button>
                <div className="vr" />
                <Button className="app-primary-color button" onClick={() => onClickCancelAddJob()}>
                  Done
                </Button>
              </Stack>
            </Col>
          </Row>
        </>
      )}
      <Row className="mb-5"></Row>
    </>
  );
};

export default Jobs;
